import React from 'react';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import SuccessGeneric from '../../../components/SuccessGeneric';
import SuccessLoyalty from '../../../components/SuccessLoyalty';
import Text from '../../../components/themed/Text';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';

type LoyaltyState = 'loyaltyPending' | 'loyaltyCreated' | 'loyaltyNone';

const AccountSuccessScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_SUCCESS>) => {
    const {rewardsStore, userStore} = useStores();

    /**
     * There are 3 potential outcomes when account (MFL) is created:
     * - zen account (loyalty) is also created.
     * - zen account creation is pending. the user has opted in but the zen service has not completed.
     * - zen account not created. user did not opt in.
     */
    let loyaltyState: LoyaltyState = rewardsStore.hasRewards
      ? 'loyaltyCreated'
      : userStore?.loyaltyPending
      ? 'loyaltyPending'
      : 'loyaltyNone';

    return (
      <ThemeProvider theme={rewardsTheme}>
        {loyaltyState === 'loyaltyNone' && (
          <SuccessGeneric
            text={`Your account is ready to go! Start shopping online now or check out our latest promotions.`}
            onPressHome={() => {
              navigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {screen: AppRoute.HOME},
              });
            }}
          />
        )}
        {loyaltyState === 'loyaltyCreated' && (
          <SuccessLoyalty
            goToHome={() => {
              navigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {screen: AppRoute.HOME},
              });
            }}
            cardNumber={rewardsStore.cardNumber}
            fullName={userStore.name}
            inStoreCode={rewardsStore.shortcode}
            walletUrl={rewardsStore.walletUrl}
            title="CONGRATULATIONS"
            text={
              <>
                <Text variant="body2">
                  Welcome {userStore.firstName} to your new account! You are now
                  also a part of our rewards program!
                  {'\n\n'}Below is your rewards card that you can access at
                  anytime in the app or through your digital wallet when you add
                  it. Show this barcode at checkout to collect awesome rewards!
                </Text>
              </>
            }
          />
        )}
        {loyaltyState === 'loyaltyPending' && (
          <SuccessLoyalty
            goToHome={() => {
              navigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {screen: AppRoute.HOME},
              });
            }}
            pending
            title="YOUR ACCOUNT HAS BEEN CREATED"
            text={
              <>
                <Text variant="body2">
                  Welcome to your new account {userStore.firstName}! A
                  confirmation has been sent to your email. Check back soon in
                  the Rewards tab to see your card ready to be used.
                </Text>
                <Text variant="heading3" style={{marginVertical: 16}}>
                  Your rewards access will be available within 24 hours
                </Text>
                <Text variant="body2">
                  Check the rewards tab in the app to see when it’s ready to go.
                </Text>
              </>
            }
          />
        )}
      </ThemeProvider>
    );
  },
);

export default AccountSuccessScreen;
